// Team state — Zustand store for restaurant team identity, membership,
// invitations, and bunker connection.
//
// Follows the v1 methodology (client/client/src/state/useTeam.ts): teams are
// NORMALIZED at load into a flat shape ({ id, name, role, keys, members,
// invitations, defaultPolicyId, supportSession }) so consumers read
// team.id / team.name / team.role directly — no per-call-site derivation.
// Member/invitation mutations live here as store actions that update shared
// state, not as ad-hoc keycastClient calls scattered across components.

import { create } from 'zustand'
import { nip19 } from 'nostr-tools'
import type { EventTemplate, VerifiedEvent } from 'nostr-tools'
import * as keycast from '@/lib/keycastClient'
import { isInternalTeam } from '@/lib/internalTeams'
import { roleFromWire } from '@synvya/auth-identity'
import type { TeamRole, TeamKey, TeamUser, Invitation } from '@/lib/keycastClient'
import * as bunker from '@/lib/bunkerSigner'
import { getDefaultRelays } from '@/lib/baseUrl'

export type { TeamRole }

const LS_ACTIVE_TEAM = 'synvya_active_team_id'
const LS_SUPPORT_SESSION_IDS = 'synvya_support_session_team_ids'
const LS_PUBKEY = 'synvya_personal_pubkey'

function npubEncode(pubkey: string): string {
  try {
    return nip19.npubEncode(pubkey)
  } catch {
    return `npub1${pubkey.slice(0, 20)}`
  }
}

// --- Support session ID tracking (released on switch-away + logout) ---

export function readSupportSessionIds(): number[] {
  try {
    const raw = localStorage.getItem(LS_SUPPORT_SESSION_IDS)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((n): n is number => typeof n === 'number') : []
  } catch {
    return []
  }
}

export function writeSupportSessionIds(ids: number[]): void {
  if (ids.length === 0) {
    localStorage.removeItem(LS_SUPPORT_SESSION_IDS)
  } else {
    localStorage.setItem(LS_SUPPORT_SESSION_IDS, JSON.stringify(ids))
  }
}

function appendSupportSessionId(id: number): void {
  const ids = readSupportSessionIds()
  if (!ids.includes(id)) writeSupportSessionIds([...ids, id])
}

export function removeSupportSessionId(id: number): void {
  writeSupportSessionIds(readSupportSessionIds().filter((n) => n !== id))
}

// --- Normalized shapes ---

/**
 * Normalized membership row. Differs from the wire `keycast.TeamUser`:
 *   - `role` is lowercase ('admin' / 'member')
 *   - `joinedAt` is the membership creation timestamp (wire: `created_at`)
 *   - `isSupportMember` is the server-stamped support-agent flag
 */
export interface TeamMember {
  user_pubkey: string
  role: TeamRole
  email?: string
  isSupportMember?: boolean
  joinedAt: string
}

function normalizeMember(wire: TeamUser): TeamMember {
  return {
    user_pubkey: wire.user_pubkey,
    role: roleFromWire(wire.role),
    email: wire.email,
    isSupportMember: wire.is_support_member,
    joinedAt: wire.created_at,
  }
}

/** Invitation roles arrive title-case on the wire, same as memberships. */
function invitationRole(invitation: Invitation): TeamRole {
  return invitation.role.toLowerCase() === 'admin' ? 'admin' : 'member'
}

export interface Team {
  id: number
  name: string
  /** Current user's role on this team. */
  role: TeamRole
  keys: TeamKey[]
  members: TeamMember[]
  invitations: Invitation[]
  /** First policy ID — needed when minting bunker authorizations. */
  defaultPolicyId: number | null
  /** True for teams held via the JIT support-access flow. Drives
   *  releaseSupportAccess cleanup on switch-away and logout. */
  supportSession?: boolean
}

function deriveRole(wire: keycast.Team, personalPubkey: string): TeamRole {
  const membership = wire.team_users?.find((u) => u.user_pubkey === personalPubkey)
  return membership ? roleFromWire(membership.role) : 'member'
}

function normalizeTeam(wire: keycast.Team, personalPubkey: string): Team {
  return {
    id: wire.team.id,
    name: wire.team.name,
    role: deriveRole(wire, personalPubkey),
    keys: wire.stored_keys ?? [],
    members: (wire.team_users ?? []).map(normalizeMember),
    invitations: [],
    defaultPolicyId: wire.policies?.[0]?.policy?.id ?? null,
  }
}

interface TeamState {
  teams: Team[]
  activeTeamId: number | null
  restaurantPubkey: string | null
  restaurantNpub: string | null
  restaurantName: string | null
  userRole: TeamRole | null
  bunkerConnected: boolean
  loading: boolean
  error: string | null

  loadTeams: () => Promise<void>
  selectTeam: (teamId: number, _triedTeamIds?: Set<number>) => Promise<void>
  /** Set active team WITHOUT network calls. Use when the caller already
   *  connected the bunker (new-restaurant setup completion). */
  activateTeam: (teamId: number) => void
  /** Connect the bunker from a URL and record the connection. */
  connectBunker: (bunkerUrl: string) => Promise<void>
  /** Register a JIT support-access team (marks supportSession) and select it. */
  addSupportSession: (params: {
    teamId: number
    teamName: string
    storedKeyPubkey: string
    bunkerUrl: string
  }) => Promise<void>

  signAsRestaurant: (template: EventTemplate) => Promise<VerifiedEvent>
  nip44EncryptAsRestaurant: (plaintext: string, peerPubkey: string) => Promise<string>
  nip44DecryptAsRestaurant: (ciphertext: string, peerPubkey: string) => Promise<string>

  addMember: (userPubkey: string, role: TeamRole) => Promise<void>
  removeMember: (userPubkey: string) => Promise<void>
  leaveActiveTeam: () => Promise<void>
  inviteMember: (email: string, role: TeamRole) => Promise<'added' | 'invited'>
  loadInvitations: () => Promise<void>
  revokeInvitation: (invitationId: number) => Promise<void>
  /** Revoke a stale invitation and issue a fresh one for the same email/role. */
  resendInvitation: (invitationId: number) => Promise<'added' | 'invited'>

  reset: () => void
}

export const useTeam = create<TeamState>((set, get) => ({
  teams: [],
  activeTeamId: null,
  restaurantPubkey: null,
  restaurantNpub: null,
  restaurantName: null,
  userRole: null,
  bunkerConnected: false,
  loading: false,
  error: null,

  loadTeams: async () => {
    set({ loading: true, error: null })
    try {
      const raw = await keycast.listTeams()
      const personalPubkey = localStorage.getItem(LS_PUBKEY) ?? ''
      // Preserve supportSession flags across reload (listTeams doesn't know them).
      const sessionIds = new Set(readSupportSessionIds())
      const teams: Team[] = raw
        .map((w) => {
          const t = normalizeTeam(w, personalPubkey)
          if (sessionIds.has(t.id)) t.supportSession = true
          return t
        })
        .filter((t) => !isInternalTeam(t.name))
      set({ teams, loading: false })
      // Auto-select is AuthGate's job (gated on adminRole).
    } catch (err) {
      if (keycast.isForbidden(err)) {
        // 403 — session valid but team access revoked. Clear stale selection.
        localStorage.removeItem(LS_ACTIVE_TEAM)
        set({ teams: [], loading: false, error: null })
      } else {
        const msg = err instanceof Error ? err.message : 'Failed to load teams'
        set({ loading: false, error: msg })
      }
    }
  },

  selectTeam: async (teamId: number, _triedTeamIds?: Set<number>) => {
    const team = get().teams.find((t) => t.id === teamId)
    if (!team) return

    // Release a prior support-session team on switch-away (v1) so JIT
    // grants don't linger until logout (#12).
    const previousId = get().activeTeamId
    if (previousId !== null && previousId !== teamId) {
      const previous = get().teams.find((t) => t.id === previousId)
      if (previous?.supportSession) {
        void keycast
          .releaseSupportAccess(previousId)
          .catch((err) => console.warn('[useTeam] releaseSupportAccess on switch failed:', err))
        set({ teams: get().teams.filter((t) => t.id !== previousId) })
        removeSupportSessionId(previousId)
      }
    }

    localStorage.setItem(LS_ACTIVE_TEAM, String(teamId))
    const expectedKey = team.keys[0]
    set({
      activeTeamId: teamId,
      restaurantName: team.name,
      userRole: team.role,
      restaurantPubkey: expectedKey?.pubkey ?? null,
      restaurantNpub: expectedKey ? npubEncode(expectedKey.pubkey) : null,
      bunkerConnected: false,
      error: null,
    })

    if (!expectedKey) {
      set({ error: 'No restaurant key found. Complete restaurant setup to connect.' })
      return
    }

    // Reconnect from stored URL (device-scoped, preserved across logout).
    const pubkey = await bunker.reconnect()
    if (pubkey) {
      if (pubkey !== expectedKey.pubkey) {
        await bunker.disconnect()
        bunker.forgetStoredBunkerUrl()
      } else {
        set({
          restaurantPubkey: pubkey,
          restaurantNpub: npubEncode(pubkey),
          bunkerConnected: true,
        })
        return
      }
    }

    // Mint a fresh per-device authorization.
    //
    // **A team with no policy cannot sign, and the merchant has to be told.**
    // This returned silently. The header showed OFFLINE with no reason, and the
    // menu editor still accepted edits that no save could carry.
    if (team.defaultPolicyId === null) {
      set({
        bunkerConnected: false,
        error:
          'This restaurant has no signing policy, so nothing can be saved. Contact support.',
      })
      return
    }
    try {
      const relays = getDefaultRelays()
      const auth = await keycast.createAuthorization(
        teamId,
        expectedKey.pubkey,
        team.defaultPolicyId,
        relays,
      )
      if (auth.bunker_url) {
        await bunker.connect(auth.bunker_url)
        set({ bunkerConnected: true })
      }
    } catch (err) {
      if (keycast.isForbidden(err)) {
        // Lost access to this team. Try at most one fallback to avoid
        // cycling through every team (support users 403 on all of them).
        localStorage.removeItem(LS_ACTIVE_TEAM)
        const alreadyRetried = _triedTeamIds !== undefined
        if (!alreadyRetried) {
          const fallback = get().teams.find((t) => t.id !== teamId)
          if (fallback) {
            await get().selectTeam(fallback.id, new Set([teamId]))
            return
          }
        }
        set({
          error: 'You no longer have access to this restaurant. Create a new one or contact the team owner.',
          bunkerConnected: false,
          activeTeamId: null,
          restaurantPubkey: null,
          restaurantNpub: null,
          restaurantName: null,
          userRole: null,
        })
      } else {
        const msg = err instanceof Error ? err.message : 'Bunker connection failed'
        set({ error: msg, bunkerConnected: false })
      }
    }
  },

  activateTeam: (teamId: number) => {
    const team = get().teams.find((t) => t.id === teamId)
    if (!team) return
    const expectedKey = team.keys[0]
    localStorage.setItem(LS_ACTIVE_TEAM, String(teamId))
    set({
      activeTeamId: teamId,
      restaurantName: team.name,
      userRole: team.role,
      restaurantPubkey: expectedKey?.pubkey ?? null,
      restaurantNpub: expectedKey ? npubEncode(expectedKey.pubkey) : null,
      bunkerConnected: bunker.isConnected(),
      error: null,
    })
  },

  connectBunker: async (bunkerUrl: string) => {
    try {
      const pubkey = await bunker.connect(bunkerUrl)
      set({
        restaurantPubkey: pubkey,
        restaurantNpub: npubEncode(pubkey),
        bunkerConnected: true,
        error: null,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Bunker connection failed'
      set({ bunkerConnected: false, error: msg })
      throw err
    }
  },

  addSupportSession: async ({ teamId, teamName, storedKeyPubkey, bunkerUrl }) => {
    // Build a placeholder Team for the JIT membership; defaultPolicyId is
    // null (support sessions don't mint client authorizations — the JIT
    // bunker URL is the only signing channel).
    const placeholder: Team = {
      id: teamId,
      name: teamName,
      role: 'member',
      keys: [{ pubkey: storedKeyPubkey, name: 'Restaurant Key' }],
      members: [],
      invitations: [],
      defaultPolicyId: null,
      supportSession: true,
    }
    set({ teams: [...get().teams.filter((t) => t.id !== teamId), placeholder] })
    appendSupportSessionId(teamId)

    // Connect first so selectTeam's reconnect path matches (no re-mint).
    await bunker.connect(bunkerUrl)
    await get().selectTeam(teamId)

    // Backfill real member data + the agent's actual role now that the JIT
    // membership exists. The placeholder assumed 'member', but the support
    // grant may make the agent an admin — derive it from the fetched rows so
    // team.role and userRole match what the staff list shows.
    try {
      const raw = await keycast.listTeams()
      const match = raw.find((t) => t.team.id === teamId)
      if (match) {
        const personalPubkey = localStorage.getItem(LS_PUBKEY) ?? ''
        const members = match.team_users.map(normalizeMember)
        const role = deriveRole(match, personalPubkey)
        set({
          teams: get().teams.map((t) => (t.id === teamId ? { ...t, members, role } : t)),
          userRole: get().activeTeamId === teamId ? role : get().userRole,
        })
      }
    } catch {
      // Non-critical — team is usable with an empty member list.
    }
  },

  signAsRestaurant: async (template: EventTemplate) => {
    if (!bunker.isConnected()) throw new Error('Bunker not connected — cannot sign events')
    return bunker.signEvent(template)
  },

  nip44EncryptAsRestaurant: async (plaintext: string, peerPubkey: string) => {
    if (!bunker.isConnected()) throw new Error('Bunker not connected — cannot encrypt')
    return bunker.nip44Encrypt(plaintext, peerPubkey)
  },

  nip44DecryptAsRestaurant: async (ciphertext: string, peerPubkey: string) => {
    if (!bunker.isConnected()) throw new Error('Bunker not connected — cannot decrypt')
    return bunker.nip44Decrypt(ciphertext, peerPubkey)
  },

  addMember: async (userPubkey: string, role: TeamRole) => {
    const teamId = get().activeTeamId
    if (!teamId) throw new Error('No active team')
    const newUser = await keycast.addTeamMember(teamId, userPubkey, role)
    set({
      teams: get().teams.map((t) =>
        t.id === teamId ? { ...t, members: [...t.members, normalizeMember(newUser)] } : t,
      ),
    })
  },

  removeMember: async (userPubkey: string) => {
    const teamId = get().activeTeamId
    if (!teamId) throw new Error('No active team')
    await keycast.removeTeamMember(teamId, userPubkey)
    set({
      teams: get().teams.map((t) =>
        t.id === teamId
          ? { ...t, members: t.members.filter((m) => m.user_pubkey !== userPubkey) }
          : t,
      ),
    })
  },

  leaveActiveTeam: async () => {
    const teamId = get().activeTeamId
    if (!teamId) throw new Error('No active team')
    const personalPubkey = localStorage.getItem(LS_PUBKEY)
    if (!personalPubkey) throw new Error('No personal pubkey on this device')
    // Keycast allows self-removal as long as another admin remains; a 403
    // means the caller is the last admin — surfaced to the UI.
    await keycast.removeTeamMember(teamId, personalPubkey)
    try {
      await bunker.disconnect()
    } catch {
      // Best-effort.
    }
    bunker.forgetStoredBunkerUrl()
    localStorage.removeItem(LS_ACTIVE_TEAM)
    removeSupportSessionId(teamId)
    set({
      teams: get().teams.filter((t) => t.id !== teamId),
      activeTeamId: null,
      restaurantPubkey: null,
      restaurantNpub: null,
      restaurantName: null,
      userRole: null,
      bunkerConnected: false,
    })
  },

  inviteMember: async (email: string, role: TeamRole) => {
    const teamId = get().activeTeamId
    if (!teamId) throw new Error('No active team')
    const res = await keycast.inviteTeamMember(teamId, email, role)
    if (res.status === 'added') {
      set({
        teams: get().teams.map((t) =>
          t.id === teamId ? { ...t, members: [...t.members, normalizeMember(res.member)] } : t,
        ),
      })
      return 'added'
    }
    set({
      teams: get().teams.map((t) =>
        t.id === teamId ? { ...t, invitations: [...t.invitations, res.invitation] } : t,
      ),
    })
    return 'invited'
  },

  loadInvitations: async () => {
    const teamId = get().activeTeamId
    if (!teamId) return
    const invitations = await keycast.listInvitations(teamId)
    set({
      teams: get().teams.map((t) => (t.id === teamId ? { ...t, invitations } : t)),
    })
  },

  revokeInvitation: async (invitationId: number) => {
    const teamId = get().activeTeamId
    if (!teamId) throw new Error('No active team')
    await keycast.revokeInvitation(teamId, invitationId)
    set({
      teams: get().teams.map((t) =>
        t.id === teamId
          ? { ...t, invitations: t.invitations.filter((i) => i.id !== invitationId) }
          : t,
      ),
    })
  },

  resendInvitation: async (invitationId: number) => {
    const teamId = get().activeTeamId
    if (!teamId) throw new Error('No active team')
    const invitation = get()
      .teams.find((t) => t.id === teamId)
      ?.invitations.find((i) => i.id === invitationId)
    if (!invitation) throw new Error('Invitation not found')
    // Keycast's uq_pending_invite index only excludes accepted/revoked rows,
    // not expired ones, so a lapsed invitation still blocks a re-invite with
    // "Already exists" (#175). Revoking drops it out of the index first.
    // If the follow-up invite fails the row is already gone — the caller
    // surfaces the email so the admin can retry from the invite form.
    await get().revokeInvitation(invitationId)
    return get().inviteMember(invitation.email, invitationRole(invitation))
  },

  reset: () => {
    bunker.disconnect()
    localStorage.removeItem(LS_ACTIVE_TEAM)
    set({
      teams: [],
      activeTeamId: null,
      restaurantPubkey: null,
      restaurantNpub: null,
      restaurantName: null,
      userRole: null,
      bunkerConnected: false,
      loading: false,
      error: null,
    })
  },
}))

/**
 * Role of the current user on the active team, or null when none is active.
 * Prefer this over walking the teams array inline in components.
 */
export function selectRole(state: Pick<TeamState, 'teams' | 'activeTeamId'>): TeamRole | null {
  const active = state.teams.find((t) => t.id === state.activeTeamId)
  return active ? active.role : null
}

export function useRole(): TeamRole | null {
  return useTeam(selectRole)
}
